import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ruler, Layers, Fence, TrendingUp, Home as HomeIcon, Blinds, Hammer, Sparkles, ExternalLink } from "lucide-react";
import { FadeIn, Reveal } from "../components/Motion";
import Seo from "../components/Seo";
import { track } from "../lib/track";

const DEFAULTS = {
  hero_overline: "Project Financing",
  hero_headline: "More Flexibility for Your Outdoor Living Project.",
  hero_body:
    "Planning a new deck, covered porch, screened porch, railing upgrade, or larger outdoor-living project? Let Zarco's know if financing is important to your project so we can discuss payment and financing options during your consultation.",
  disclosure:
    "Financing availability is subject to third-party lender programs, applicant qualification, and program availability. Zarco's Decks & Outdoor Living is not a lender.",
  provider_name: "Acorn Finance",
  provider_active: false,
  provider_url: "",
  provider_disclosure: "",
  provider_logo: "",
};

const FACTORS = [
  { icon: Ruler, label: "Project size" },
  { icon: Layers, label: "Decking collection" },
  { icon: Fence, label: "Railing" },
  { icon: TrendingUp, label: "Stairs" },
  { icon: HomeIcon, label: "Roof & porch additions" },
  { icon: Blinds, label: "Screening" },
  { icon: Hammer, label: "Structural work" },
  { icon: Sparkles, label: "Custom features" },
];

const STEPS = [
  {
    n: "01",
    title: "Plan Your Project",
    body: "Meet with Zarco's and determine the design, materials, scope, and project estimate — with real samples and a written proposal.",
  },
  {
    n: "02",
    title: "Tell Us Financing Matters",
    body: "Let Zarco's know financing is an important part of your purchasing decision — simply mention it when requesting your free estimate.",
  },
  {
    n: "03",
    title: "Review Available Options",
    body: "When an approved third-party financing program is available for the project, qualified homeowners can review the provider's available options.",
  },
];

export default function Financing() {
  const [c, setC] = useState(DEFAULTS);

  useEffect(() => {
    track("financing_page_view");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/financing`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setC({ ...DEFAULTS, ...d }))
      .catch(() => {});
  }, []);

  const providerLive = !!(c.provider_active && c.provider_url);

  return (
    <div data-testid="financing-page" className="bg-white">
      <Seo
        title="Project Financing | Zarco's Decks & Outdoor Living — North Georgia"
        description="Financing can matter on larger deck, porch, and outdoor-living projects. Tell Zarco's Decks if financing is important to your project when you request your free estimate."
      />

      {/* HERO — premium light treatment */}
      <section className="relative bg-[#FBF8F3] text-[#111] pt-32 md:pt-40 pb-20 md:pb-28 px-6 md:px-12 lg:px-20 overflow-hidden border-b border-[#E7E2D9]">
        <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#E8D6C2]/40 blur-3xl" />
        <FadeIn className="relative max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#99582A] font-semibold">
            {c.hero_overline}
          </p>
          <h1
            data-testid="financing-headline"
            className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl tracking-tighter font-medium leading-[0.98] text-[#111]"
          >
            {c.hero_headline}
          </h1>
          <p data-testid="financing-intro" className="mt-7 text-base md:text-lg text-[#4A4A4A] leading-relaxed">
            {c.hero_body}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="financing-cta-estimate"
              className="group bg-[#111] text-white hover:bg-[#99582A] transition-colors px-8 py-4 font-semibold tracking-wide inline-flex items-center gap-3"
            >
              Get Your Free Estimate
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
            <Link
              to="/contact?financing=1"
              data-testid="financing-cta-interest"
              onClick={() => track("financing_interest_click")}
              className="border-2 border-[#99582A] text-[#99582A] hover:bg-[#99582A] hover:text-white transition-colors px-8 py-4 font-semibold tracking-wide"
            >
              I'm Interested in Financing
            </Link>
            {providerLive && (
              <a
                href={c.provider_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="financing-cta-provider"
                onClick={() => { track("financing_provider_click", { provider: c.provider_name }); track("provider_financing_click", { provider: c.provider_name, location: "financing_page" }); }}
                className="border-2 border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition-colors px-8 py-4 font-semibold tracking-wide inline-flex items-center gap-2"
              >
                Check Financing Options <ExternalLink size={16} />
              </a>
            )}
          </div>
          {providerLive && c.provider_disclosure ? (
            <p data-testid="financing-provider-disclosure" className="mt-5 text-xs text-[#666] max-w-xl leading-relaxed">
              {c.provider_disclosure}
            </p>
          ) : null}
        </FadeIn>
      </section>

      {/* LARGER PROJECT — cost factors, no pricing published */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">Budget Planning</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111] max-w-3xl">
            Larger project. More ways to plan for it.
          </h2>
          <p className="mt-6 text-[#4A4A4A] leading-relaxed max-w-2xl">
            Outdoor-living projects can range significantly depending on the choices you make.
            Every Zarco's estimate spells out exactly what drives your project's investment —
            before you commit to anything.
          </p>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {FACTORS.map(({ icon: Icon, label }, i) => (
            <Reveal
              key={label}
              delay={i * 0.06}
              data-testid={`financing-factor-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
              className="zd-card-lift group bg-white border border-[#E7E2D9] p-6 md:p-8"
            >
              <div className="w-11 h-11 bg-[#FBF8F3] border border-[#E7E2D9] flex items-center justify-center transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105">
                <Icon size={18} strokeWidth={1.5} className="text-[#99582A]" />
              </div>
              <p className="font-display text-lg tracking-tight font-medium mt-5 text-[#111]">{label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="financing-how-it-works" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#FBF8F3]">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">How It Works</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111]">
            Three simple steps.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1} className="h-full">
              <div className="h-full bg-white border border-[#E7E2D9] p-8 md:p-10">
                <span className="font-display text-3xl tracking-tight font-medium text-[#99582A]">{s.n}</span>
                <h3 className="font-display text-2xl tracking-tight font-medium mt-5 text-[#111]">{s.title}</h3>
                <p className="mt-4 text-[#4A4A4A] leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-12">
          <Link
            to="/contact?financing=1"
            data-testid="financing-bottom-cta"
            onClick={() => track("financing_interest_click", { location: "how_it_works" })}
            className="group inline-flex items-center gap-3 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-9 py-4 font-semibold tracking-wide"
          >
            I'm Interested in Financing
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </section>

      {/* DISCLOSURE — small, tasteful */}
      <section className="px-6 md:px-12 lg:px-20 py-10 bg-white border-t border-[#E7E2D9]">
        <p data-testid="financing-disclosure" className="text-xs text-[#999] leading-relaxed max-w-3xl">
          {c.disclosure}
        </p>
      </section>
    </div>
  );
}
